import { PaginationProps, TableProps } from 'antd';
import { deleteApi, getApi } from 'apis';
import PopUpWrapper from 'components/Global/PopUpWrapper';
import TableActionHeader from 'components/Global/TableActionHeader';
import ViewPopUpWrapper from 'components/Global/ViewPopUpWrapper';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import { Dayjs } from 'dayjs';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { checkPermission, toastText } from 'utils/utils';
import AddEditGroceryBody from './AddEditGroceryBody';
import CustomTable from './CustomTable';
import ViewGroceryBody from './ViewGroceryBody';
import { useDebounce } from 'utils/hooks';
import {
  StatusFilterOption,
} from './SearchAndFilter/types';

export const GroceriesTable = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(
    Number(localStorage.getItem('groceryTableSize')) || 10,
  );
  const [sortField, setSortField] = useState('createdAt');
  const [sortType, setSortType] = useState('desc');
  const [selectedItem, setSelectedItem] = useState<any>();
  const [editSelectedGrocery, setEditSelectedGrocery] = useState<any>();
  const [title, setTitle] = useState('Add Grocery');
  const [totalItems, setTotalItems] = useState(0);
  const [groceryData, setGroceryData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isPoppedUp, setIsPoppedUp] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption | null>(null);
  const [selectedDate, setSelectedDate] = useState<[Dayjs, Dayjs] | null>(null);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [optionsState, setOptionsState] = useState({
    productName: [],
    brand: [],
    manufacturerName: [],
    marketedBy: [],
    storageInstructions: [],
    fssaiLicenseNo: [],
    hsnCode: [],
    productCode: [],
    shelfLife: [],
    numberOfItems: [],
    ingredients: [],
  });

  const debouncedSearch = useDebounce(search, 500);

  const groceryPermissions = useSelector(
    (state: any) => state?.userProfile?.data,
  );

  const canUseUserFilter =
    groceryPermissions?.isAdmin === true ||
    checkPermission(groceryPermissions?.permissions, {
      permissionName: 'GroceryItemsUserFilter',
      permission: ['view'],
    });

  const viewHandler = (item: any) => {
    setSelectedItem(item);
    setIsViewOpen(true);
  };

  const openDrawerHandler = () => {
    setTitle('Add Grocery');
    setEditSelectedGrocery(null);
    setIsPoppedUp(true);
  };

  const closeDrawerByAnimation = () => {
    setIsPoppedUp(false);
    setEditSelectedGrocery(null);
    setOptionsState({
      productName: [],
      brand: [],
      manufacturerName: [],
      marketedBy: [],
      storageInstructions: [],
      fssaiLicenseNo: [],
      hsnCode: [],
      productCode: [],
      shelfLife: [],
      numberOfItems: [],
      ingredients: [],
    });
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditSelectedGrocery(null);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    setEditSelectedGrocery(null);
  };

  const deleteHandler = async () => {
    if (!editSelectedGrocery?.id) return;
    try {
      setIsDeleteLoading(true);
      const resp = await deleteApi(`/grocery/delete/${editSelectedGrocery.id}`);
      toastText(
        resp?.data?.message ?? 'Grocery item deleted successfully',
        'success',
      );
    } catch (error: any) {
      toastText(
        error?.response?.data?.message ??
          'Something went wrong while deleting grocery item',
        'error',
      );
    } finally {
      setIsDeleteLoading(false);
      handleCancel();
      fetchGroceries();
    }
  };

  const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, selectedDate, selectedUser]);

  const fetchAllUsers = async () => {
    if (!canUseUserFilter) return;

    await getApi('/user', { staffActive: 'true', limit: 1000 })
      .then((res: any) => {
        if (res?.data?.responseStatus === 200) {
          const users = res?.data?.data;
          const filteredUsers = users
            ?.filter((item: any) => item?.staffActive !== false && item?.status !== false)
            ?.map((item: any) => ({
              userId: item.userId,
              name: item.name,
              status: item.status,
            }));
          setAllUsers(filteredUsers);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (groceryPermissions && !isInitialized) {
      setSelectedUser(null);
      setIsInitialized(true);
    }
  }, [groceryPermissions, isInitialized]);

  useEffect(() => {
    if (canUseUserFilter) {
      fetchAllUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAllUsers is stable
  }, [groceryPermissions, canUseUserFilter]);

  const fetchGroceries = useCallback(async () => {
    setIsLoading(true);
    const query: Record<string, unknown> = {
      page,
      limit,
      search: debouncedSearch,
      sorterField: sortField,
      type: sortType,
      targetField: 'productName',
      selectedDate,
    };
    if (canUseUserFilter && selectedUser) {
      query.userId = selectedUser;
    }
    if (statusFilter) query.status = statusFilter;

    try {
      const res = await getApi('/grocery/get-all', query);
      setGroceryData(res?.data?.data || []);
      setTotalItems(res?.data?.total || 0);
    } catch (error: any) {
      toastText(
        error?.response?.data?.error?.errorDescription ||
          'Failed to fetch grocery items',
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    page,
    limit,
    debouncedSearch,
    sortField,
    sortType,
    statusFilter,
    selectedDate,
    selectedUser,
    canUseUserFilter,
  ]);

  useEffect(() => {
    if (isInitialized) {
      fetchGroceries();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchGroceries is stable
  }, [fetchGroceries, isInitialized]);

  const handleTableChange: TableProps<any>['onChange'] = (_pagination, _filters, sorter) => {
    const sortOrder = Array.isArray(sorter)
      ? sorter[0]?.order
      : sorter.order === 'ascend'
        ? 'asc'
        : 'desc';
    setSortType(sortOrder || 'asc');

    const field = Array.isArray(sorter) ? sorter[0]?.field : sorter.field;
    setSortField((field as string) || 'productName');
  };

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (current, pageSize) => {
    setPage(current);
    setLimit(pageSize);
    localStorage.setItem('groceryTableSize', String(pageSize));
  };

  return (
    <>
      <div>
        <TableActionHeader title="Groceries" />
        <CustomTable
          dataSource={groceryData}
          handleTableChange={handleTableChange}
          onShowSizeChange={onShowSizeChange}
          isLoading={isLoading}
          search={search}
          totalRecords={totalItems}
          performSearchHandler={performSearchHandler}
          currentPage={page}
          pageSize={limit}
          setSearch={setSearch}
          viewHandler={viewHandler}
          openDrawerHandler={openDrawerHandler}
          setEditSelectedGrocery={setEditSelectedGrocery}
          setTitle={setTitle}
          showModal={showModal}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          allUsers={allUsers}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
        />
      </div>

      <ConfirmDelete
        handleCancel={handleCancel}
        handleOk={handleOk}
        isModalOpen={isModalOpen}
        deleteHandler={deleteHandler}
        isLoading={isDeleteLoading}
        selectedDeleteData={editSelectedGrocery?.productName}
        deleteHeading="Grocery"
      />

      {isPoppedUp && (
        <PopUpWrapper
          title={title}
          isPoppedUp={isPoppedUp}
          closeDrawerByAnimation={closeDrawerByAnimation}
        >
          <AddEditGroceryBody
            closeDrawerByAnimation={closeDrawerByAnimation}
            editSelectedGrocery={editSelectedGrocery}
            setEditSelectedGrocery={setEditSelectedGrocery}
            fetchGroceries={fetchGroceries}
            optionsState={optionsState}
            setOptionsState={setOptionsState}
          />
        </PopUpWrapper>
      )}

      {isViewOpen && (
        <ViewPopUpWrapper
          title="Grocery Details"
          isPoppedUp={isViewOpen}
          closeDrawerByAnimation={() => {
            setIsViewOpen(false);
            setSelectedItem(null);
          }}
        >
          <ViewGroceryBody grocery={selectedItem} />
        </ViewPopUpWrapper>
      )}
    </>
  );
};

export default GroceriesTable;
